/**
 * RateAssignmentDrawer - Slide-out drawer for creating/editing time-bounded rate assignments
 */

import { useState, useEffect } from 'react';
import { AdminFormDrawer } from './AdminFormDrawer';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, AlertCircle, Calendar } from 'lucide-react';
import { cn } from '@/lib/utils';
import type {
  EnergyRate,
  RateAssignmentRecord,
  RateAssignmentFormData,
} from '@/types/rates';
import { format, parseISO } from 'date-fns';

interface RateAssignmentDrawerProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: RateAssignmentFormData) => Promise<void>;
  assignment?: RateAssignmentRecord | null;
  entityType: 'company' | 'group' | 'unit';
  entityId: string;
  entityName: string;
  rates: EnergyRate[];
  isSubmitting?: boolean;
}

export function RateAssignmentDrawer({
  open,
  onClose,
  onSubmit,
  assignment,
  entityType,
  entityId,
  entityName,
  rates,
  isSubmitting = false,
}: RateAssignmentDrawerProps) {
  const isEditing = !!assignment;

  // Form state
  const [formData, setFormData] = useState<{
    rateId: string;
    effectiveFrom: Date | undefined;
    effectiveTo: Date | undefined;
    isOverride: boolean;
    hasEndDate: boolean;
  }>({
    rateId: '',
    effectiveFrom: new Date(),
    effectiveTo: undefined,
    isOverride: false,
    hasEndDate: false,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Initialize form when assignment changes
  useEffect(() => {
    if (assignment) {
      setFormData({
        rateId: assignment.rateId,
        effectiveFrom: parseISO(assignment.effectiveFrom),
        effectiveTo: assignment.effectiveTo ? parseISO(assignment.effectiveTo) : undefined,
        isOverride: assignment.isOverride,
        hasEndDate: !!assignment.effectiveTo,
      });
    } else {
      // Reset to defaults for new assignment
      setFormData({
        rateId: rates[0]?.id || '',
        effectiveFrom: new Date(),
        effectiveTo: undefined,
        isOverride: false,
        hasEndDate: false,
      });
    }
    setErrors({});
  }, [assignment, open, rates]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.rateId) {
      newErrors.rateId = 'Please select a rate profile';
    }

    if (!formData.effectiveFrom) {
      newErrors.effectiveFrom = 'Start date is required';
    }

    if (formData.hasEndDate && formData.effectiveTo && formData.effectiveFrom) {
      if (formData.effectiveTo < formData.effectiveFrom) {
        newErrors.effectiveTo = 'End date must be after start date';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    const submitData: RateAssignmentFormData = {
      rateId: formData.rateId,
      entityType,
      entityId,
      effectiveFrom: formData.effectiveFrom
        ? format(formData.effectiveFrom, 'yyyy-MM-dd')
        : format(new Date(), 'yyyy-MM-dd'),
      effectiveTo: formData.hasEndDate && formData.effectiveTo
        ? format(formData.effectiveTo, 'yyyy-MM-dd')
        : null,
      isOverride: entityType === 'unit' ? formData.isOverride : false,
    };

    await onSubmit(submitData);
  };

  const selectedRate = rates.find(r => r.id === formData.rateId);

  const entityTypeLabels = {
    company: 'Company',
    group: 'Group/Site',
    unit: 'Unit',
  };

  return (
    <AdminFormDrawer
      open={open}
      onClose={onClose}
      title={isEditing ? 'Edit Rate Assignment' : 'Assign Rate'}
      description={isEditing
        ? `Update rate assignment for ${entityName}`
        : `Assign a rate profile to ${entityName}`
      }
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Entity Info */}
        <div className="bg-muted/50 rounded-lg p-4">
          <div className="text-sm text-muted-foreground">
            {entityTypeLabels[entityType]}
          </div>
          <div className="font-medium text-lg">{entityName}</div>
        </div>

        {/* Rate Selection */}
        <div className="space-y-2">
          <Label htmlFor="rateId">Rate Profile *</Label>
          <Select
            value={formData.rateId}
            onValueChange={value => {
              setFormData(prev => ({ ...prev, rateId: value }));
              if (errors.rateId) {
                setErrors(prev => {
                  const { rateId: _, ...rest } = prev;
                  return rest;
                });
              }
            }}
          >
            <SelectTrigger className={cn(errors.rateId && 'border-destructive')}>
              <SelectValue placeholder="Select a rate profile" />
            </SelectTrigger>
            <SelectContent>
              {rates.map(rate => (
                <SelectItem key={rate.id} value={rate.id}>
                  <div className="flex flex-col">
                    <span>{rate.name}</span>
                    <span className="text-xs text-muted-foreground">
                      Import: ${rate.gridImportRate}/kWh | Sell: ${rate.gridSellRate}/kWh
                    </span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.rateId && <p className="text-sm text-destructive">{errors.rateId}</p>}
        </div>

        {/* Selected Rate Preview */}
        {selectedRate && (
          <div className="border border-border rounded-lg p-4 space-y-2">
            <h3 className="font-semibold text-sm">{selectedRate.name}</h3>
            {selectedRate.description && (
              <p className="text-sm text-muted-foreground">{selectedRate.description}</p>
            )}
            <div className="grid grid-cols-2 gap-4 text-sm pt-2">
              <div>
                <span className="text-muted-foreground">Grid Import:</span>
                <span className="ml-2 font-mono">${selectedRate.gridImportRate}/kWh</span>
              </div>
              <div>
                <span className="text-muted-foreground">Grid Sell:</span>
                <span className="ml-2 font-mono">${selectedRate.gridSellRate}/kWh</span>
              </div>
              {selectedRate.utilityProvider && (
                <div>
                  <span className="text-muted-foreground">Provider:</span>
                  <span className="ml-2">{selectedRate.utilityProvider}</span>
                </div>
              )}
              {selectedRate.stateProvince && (
                <div>
                  <span className="text-muted-foreground">State:</span>
                  <span className="ml-2">{selectedRate.stateProvince}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Effective Period */}
        <div className="border border-border rounded-lg p-4 space-y-4">
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <h3 className="font-semibold text-sm">Effective Period</h3>
          </div>

          {/* Start Date */}
          <div className="space-y-2">
            <Label>Start Date *</Label>
            <DatePicker
              date={formData.effectiveFrom}
              onDateChange={date => {
                setFormData(prev => ({ ...prev, effectiveFrom: date }));
                if (errors.effectiveFrom) {
                  setErrors(prev => {
                    const { effectiveFrom: _, ...rest } = prev;
                    return rest;
                  });
                }
              }}
              placeholder="Select start date"
              className={cn(errors.effectiveFrom && 'border-destructive')}
            />
            {errors.effectiveFrom && (
              <p className="text-sm text-destructive">{errors.effectiveFrom}</p>
            )}
          </div>

          {/* Has End Date Toggle */}
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="hasEndDate" className="text-sm font-medium">
                Set End Date
              </Label>
              <p className="text-xs text-muted-foreground mt-1">
                Leave unchecked for ongoing assignment
              </p>
            </div>
            <Switch
              id="hasEndDate"
              checked={formData.hasEndDate}
              onCheckedChange={checked => {
                setFormData(prev => ({
                  ...prev,
                  hasEndDate: checked,
                  effectiveTo: checked ? prev.effectiveTo : undefined,
                }));
              }}
            />
          </div>

          {/* End Date (conditional) */}
          {formData.hasEndDate && (
            <div className="space-y-2">
              <Label>End Date</Label>
              <DatePicker
                date={formData.effectiveTo}
                onDateChange={date => {
                  setFormData(prev => ({ ...prev, effectiveTo: date }));
                  if (errors.effectiveTo) {
                    setErrors(prev => {
                      const { effectiveTo: _, ...rest } = prev;
                      return rest;
                    });
                  }
                }}
                placeholder="Select end date"
                className={cn(errors.effectiveTo && 'border-destructive')}
              />
              {errors.effectiveTo && (
                <p className="text-sm text-destructive">{errors.effectiveTo}</p>
              )}
            </div>
          )}
        </div>

        {/* Override Toggle (only for units) */}
        {entityType === 'unit' && (
          <div className="flex items-center justify-between border border-border rounded-lg p-4">
            <div>
              <Label htmlFor="isOverride" className="text-sm font-medium">
                Override Inherited Rate
              </Label>
              <p className="text-xs text-muted-foreground mt-1">
                When enabled, this rate takes precedence over inherited rates from group/company
              </p>
            </div>
            <Switch
              id="isOverride"
              checked={formData.isOverride}
              onCheckedChange={checked =>
                setFormData(prev => ({ ...prev, isOverride: checked }))
              }
            />
          </div>
        )}

        {/* Warning for overlapping */}
        <div className="flex items-start gap-2 bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
          <AlertCircle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-500">
            Rate assignments cannot overlap. If you try to create an assignment that overlaps
            with an existing one, it will be rejected.
          </p>
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-4 border-t border-border">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            className="flex-1"
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            className="flex-1 bg-[#8FB560] hover:bg-[#7a9d4e]"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {isEditing ? 'Updating...' : 'Assigning...'}
              </>
            ) : (
              isEditing ? 'Update Assignment' : 'Assign Rate'
            )}
          </Button>
        </div>
      </form>
    </AdminFormDrawer>
  );
}
